import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge, Button, Card, Divider, Group, Loader, MultiSelect,
  Pagination, Select, SegmentedControl, Stack, Text, Title,
} from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import HttpClient from '../helpers/HttpClient';
import { Contract } from '../types/contract.type';
import { ContractType } from '../types/ContractType';
import dayjs from 'dayjs';

const PAGE_SIZE = 50;

const TYPE_LABELS: Record<ContractType, string> = {
  [ContractType.PLAYER]: 'Spieler',
  [ContractType.COACH]: 'Trainer',
  [ContractType.STAFF]: 'Staff',
  [ContractType.OTHER]: 'Sonstige',
};

const TYPE_COLORS: Record<ContractType, string> = {
  [ContractType.PLAYER]: 'blue',
  [ContractType.COACH]: 'teal',
  [ContractType.STAFF]: 'violet',
  [ContractType.OTHER]: 'gray',
};

function formatDate(d?: Date | string | null): string {
  if (!d) return '–';
  return dayjs(d).format('DD.MM.YYYY');
}

function AmountBadge({ label, value }: { label: string; value: number }) {
  if (!value) return null;
  return (
    <Badge size="sm" variant="light" color="gray">
      {label}: €{value}
    </Badge>
  );
}

function ContractList() {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const [filterPersonId, setFilterPersonId] = useState<string | null>(null);
  const [filterTypes, setFilterTypes] = useState<string[]>([]);
  const [filterValidity, setFilterValidity] = useState<'valid' | 'invalid' | 'all'>('valid');

  // Person options derived from loaded contracts (updated when no person filter is active)
  const [personOptions, setPersonOptions] = useState<{ value: string; label: string }[]>([]);
  const personOptionsRef = useRef<{ value: string; label: string }[]>([]);

  const fetchContracts = useCallback((currentPage: number, personId: string | null, types: string[], validity: string) => {
    setLoading(true);
    const params: Record<string, string> = {
      page: currentPage.toString(),
      pageSize: PAGE_SIZE.toString(),
      validity: validity === 'invalid' ? 'expired' : validity,
    };
    if (personId) params.personId = personId;
    if (types.length) params.types = types.join(',');

    new HttpClient<Contract>('/contract').getAllPaged(params)
      .then((res) => {
        setContracts(res.data);
        setTotal(res.total);

        if (!personId) {
          const seen = new Map<string, string>();
          res.data.forEach((c) => {
            if (c.person) {
              seen.set(c.person.id.toString(), `${c.person.firstName} ${c.person.lastName}`);
            }
          });
          seen.forEach((label, value) => personOptionsRef.current = [
            ...personOptionsRef.current.filter((o) => o.value !== value),
            { value, label },
          ]);
          setPersonOptions([...personOptionsRef.current].sort((a, b) => a.label.localeCompare(b.label)));
        }
      })
      .finally(() => setLoading(false));
  }, []);

  // Reset page and refetch when filters change
  useEffect(() => {
    setPage(1);
    fetchContracts(1, filterPersonId, filterTypes, filterValidity);
  }, [filterPersonId, filterTypes, filterValidity, fetchContracts]);

  // Refetch when page changes (without resetting page)
  useEffect(() => {
    fetchContracts(page, filterPersonId, filterTypes, filterValidity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const typeOptions = Object.values(ContractType).map((v) => ({ value: v, label: TYPE_LABELS[v] }));
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <Stack p="md" gap="md">
      <Group justify="space-between" align="center">
        <Title order={2}>Verträge</Title>
        <Button leftSection={<IconPlus size={16} />} size="md" onClick={() => navigate('/contract/create')}>
          Neu
        </Button>
      </Group>

      <SegmentedControl
        value={filterValidity}
        onChange={(v) => setFilterValidity(v as 'valid' | 'invalid' | 'all')}
        data={[
          { value: 'valid', label: 'Gültig' },
          { value: 'invalid', label: 'Abgelaufen' },
          { value: 'all', label: 'Alle' },
        ]}
        size="sm"
      />

      <Group grow wrap="wrap" gap="sm">
        <Select
          label="Person"
          data={personOptions}
          value={filterPersonId}
          onChange={setFilterPersonId}
          searchable
          clearable
          size="sm"
          placeholder="Alle Personen"
        />
        <MultiSelect
          label="Typ"
          data={typeOptions}
          value={filterTypes}
          onChange={setFilterTypes}
          clearable
          size="sm"
          placeholder="Alle Typen"
        />
      </Group>

      <Divider />

      {loading && <Loader mt="xl" />}

      {!loading && contracts.length === 0 && (
        <Text c="dimmed" ta="center" py="xl">Keine Verträge gefunden</Text>
      )}

      {!loading && contracts.map((c) => {
        const expired = c.validTo && dayjs(c.validTo).isBefore(dayjs(), 'day');
        return (
          <Card
            key={c.id}
            shadow="sm"
            padding="md"
            radius="md"
            withBorder
            style={{ cursor: 'pointer', opacity: expired ? 0.5 : 1 }}
            onClick={() => navigate(`/contract/edit/${c.id}`)}
          >
            <Group justify="space-between" mb="xs" wrap="wrap" gap="xs">
              <Group gap="xs">
                <Badge color={TYPE_COLORS[c.contractType] ?? 'gray'} variant="light" size="md">
                  {TYPE_LABELS[c.contractType] ?? c.contractType}
                </Badge>
              </Group>
              <Text size="xs" c="dimmed">
                {formatDate(c.validFrom)} – {formatDate(c.validTo)}
              </Text>
            </Group>

            <Text fw={600} fz="lg">
              {c.person ? `${c.person.firstName} ${c.person.lastName}` : <Text span c="dimmed">Keine Person</Text>}
            </Text>

            <Group gap="xs" mt="xs" wrap="wrap">
              <AmountBadge label="Fixum" value={c.fixedPerMonthAmount} />
              <AmountBadge label="Auflauf" value={c.startingSquadAmount} />
              <AmountBadge label="Einwechslung" value={c.substitionInAmount} />
              <AmountBadge label="Punkt" value={c.perPointAmount} />
              <AmountBadge label="Kader" value={c.perSquadAmount} />
            </Group>
            {c.paymentAccount && (
              <Group gap="xs" mt={4}>
                <Badge size="sm" variant="light" color="blue" leftSection="🏦">
                  {c.paymentAccount.name}
                </Badge>
              </Group>
            )}
          </Card>
        );
      })}

      {totalPages > 1 && (
        <Group justify="center" mt="md">
          <Pagination total={totalPages} value={page} onChange={setPage} size="sm" />
        </Group>
      )}
    </Stack>
  );
}

export default ContractList;
